import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { LIFECYCLE_STEPS } from 'shared/utils/lifecycle';

/**
 * LifecycleStepper
 *
 * Reusable horizontal/vertical stepper that visualises the 11-step
 * farmer lifecycle.  Completed → green ✓, current → blue dot,
 * future → gray circle.  Wraps to vertical on mobile via Tailwind.
 *
 * @param {Object}  props
 * @param {number}  props.currentStep   – Active step (1–11)
 * @param {Array}   [props.stepsData]   – Optional override labels
 *   Shape: [{ step: number, label: string, key?: string }, …]
 */
const LifecycleStepper = ({ currentStep = 1, stepsData }) => {
  const steps = stepsData || LIFECYCLE_STEPS;

  return (
    <div className="w-full px-2 py-4">
      {/* ────── Desktop / Tablet: horizontal layout ────── */}
      <div className="hidden md:flex items-start justify-between w-full">
        {steps.map((s, idx) => {
          const isCompleted = s.step < currentStep;
          const isCurrent   = s.step === currentStep;
          const isLast      = idx === steps.length - 1;

          return (
            <React.Fragment key={s.step}>
              {/* Step node */}
              <div className="flex flex-col items-center min-w-[64px] max-w-[90px]">
                {/* Circle */}
                <div
                  className={[
                    'flex items-center justify-center',
                    'w-9 h-9 rounded-full',
                    'transition-all duration-500 ease-in-out',
                    isCompleted
                      ? 'bg-green-500 text-white shadow-md shadow-green-200'
                      : isCurrent
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-110'
                        : 'bg-gray-200 text-gray-400',
                  ].join(' ')}
                >
                  {isCompleted ? (
                    <CheckIcon style={{ fontSize: 20 }} />
                  ) : isCurrent ? (
                    <FiberManualRecordIcon style={{ fontSize: 14 }} />
                  ) : (
                    <RadioButtonUncheckedIcon style={{ fontSize: 18 }} />
                  )}
                </div>

                {/* Label */}
                <span
                  className={[
                    'mt-2 text-[11px] leading-tight text-center',
                    'transition-colors duration-300',
                    isCompleted
                      ? 'text-green-700 font-semibold'
                      : isCurrent
                        ? 'text-blue-700 font-bold'
                        : 'text-gray-400',
                  ].join(' ')}
                >
                  {s.label}
                </span>
              </div>

              {/* Connector bar */}
              {!isLast && (
                <div className="flex-1 flex items-center pt-[14px] mx-1">
                  <div className="relative w-full h-[3px] rounded-full bg-gray-200">
                    <div
                      className={[
                        'absolute inset-y-0 left-0 rounded-full',
                        'transition-all duration-700 ease-in-out',
                        isCompleted ? 'bg-green-500' : 'bg-gray-200',
                      ].join(' ')}
                      style={{ width: isCompleted ? '100%' : '0%' }}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ────── Mobile: vertical layout ────── */}
      <div className="flex md:hidden flex-col items-start w-full pl-2">
        {steps.map((s, idx) => {
          const isCompleted = s.step < currentStep;
          const isCurrent   = s.step === currentStep;
          const isLast      = idx === steps.length - 1;

          return (
            <div key={s.step} className="flex items-stretch">
              {/* Left column: circle + vertical connector */}
              <div className="flex flex-col items-center">
                {/* Circle */}
                <div
                  className={[
                    'flex items-center justify-center',
                    'w-8 h-8 rounded-full flex-shrink-0',
                    'transition-all duration-500 ease-in-out',
                    isCompleted
                      ? 'bg-green-500 text-white shadow-md shadow-green-200'
                      : isCurrent
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-110'
                        : 'bg-gray-200 text-gray-400',
                  ].join(' ')}
                >
                  {isCompleted ? (
                    <CheckIcon style={{ fontSize: 18 }} />
                  ) : isCurrent ? (
                    <FiberManualRecordIcon style={{ fontSize: 12 }} />
                  ) : (
                    <RadioButtonUncheckedIcon style={{ fontSize: 16 }} />
                  )}
                </div>

                {/* Vertical connector */}
                {!isLast && (
                  <div className="w-[3px] flex-1 min-h-[28px] rounded-full my-1 relative bg-gray-200">
                    <div
                      className={[
                        'absolute inset-x-0 top-0 rounded-full',
                        'transition-all duration-700 ease-in-out',
                        isCompleted ? 'bg-green-500' : 'bg-gray-200',
                      ].join(' ')}
                      style={{ height: isCompleted ? '100%' : '0%' }}
                    />
                  </div>
                )}
              </div>

              {/* Right column: label */}
              <div className="ml-3 pb-5 flex items-start pt-1">
                <span
                  className={[
                    'text-sm leading-tight',
                    'transition-colors duration-300',
                    isCompleted
                      ? 'text-green-700 font-semibold'
                      : isCurrent
                        ? 'text-blue-700 font-bold'
                        : 'text-gray-400',
                  ].join(' ')}
                >
                  {s.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LifecycleStepper;
